import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { TextSkeleton } from 'src/components/shared/TextSkeleton'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DeleteInsurancePolicyModal } from 'src/components/insurance-policy/delete/DeleteInsurancePolicyModal'
import { InsurancePoliciesTable } from 'src/components/insurance-policy/policies-table/InsurancePoliciesTable'
import { InsurancePoliciesTableToolbar } from 'src/components/insurance-policy/policies-table/InsurancePoliciesTableToolbar'
import { InsurancePolicyFormModal } from 'src/components/insurance-policy/policy-form/InsurancePolicyFormModal'
import type {
  InsurancePolicyFormMode,
  InsurancePolicyFormValues,
} from 'src/components/insurance-policy/policy-form/InsurancePolicyForm.interface'
import { Button } from 'src/components/shared/Button'
import {
  AdminCreateInsurancePolicy,
  AdminDeleteInsurancePolicy,
  AdminFetchInsurancePolicies,
  AdminFetchInsuranceProducts,
  AdminUpdateInsurancePolicy,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { buildCustomerInsurancePolicyPath } from 'src/pages/admin-dashboard/adminDashboardNav'
import type { InsurancePolicy } from 'src/interfaces/insurancePolicy'
import {
  getInsurancePoliciesError,
  getInsurancePoliciesLoadedPartnerId,
  getInsurancePolicyDeletingPermanentId,
  getInsurancePolicySaving,
} from 'src/selectors/insurancePolicy'
import { GSIcon } from 'src/components/shared/GSIcon'

interface PolicyFormModalState {
  mode: InsurancePolicyFormMode
  policy: InsurancePolicy | null
}

const CustomerInsurancePageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by Refresh and by every successful mutation — included in the fetch effect's
  // dependency array below so any of them re-triggers the requests.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [policyFormModalState, setPolicyFormModalState] = useState<PolicyFormModalState | null>(
    null,
  )
  const [policyPendingDelete, setPolicyPendingDelete] = useState<InsurancePolicy | null>(null)

  const { isAuthenticated } = useAuthSession()
  const loadedPartnerId = useSelector(getInsurancePoliciesLoadedPartnerId)
  const policiesError = useSelector(getInsurancePoliciesError)
  const isSaving = useSelector(getInsurancePolicySaving)
  const deletingPermanentId = useSelector(getInsurancePolicyDeletingPermanentId)
  // New policy, Rename and Delete all call `insurance_policies_manage` endpoints.
  const canManagePolicies = useHasPermission(InternalPermission.InsurancePoliciesManage)
  // The global catalog only backfills chip names here. Without `insurance_products_view` its GET is
  // skipped and the chips show product ids (spec §6).
  const canViewInsuranceProducts = useHasPermission(InternalPermission.InsuranceProductsView)

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  /**
   * Two fetches in one effect: the policies, and the global insurance-product catalog. The
   * catalog is what backfills product names into the "Products in policy" chips and makes
   * search-by-product-name work — see `InsurancePoliciesTable`. The DocuSign template fetch the
   * old combined page ran lives on `CustomerInsurancePolicyPage` now: nothing on this page reads
   * a template.
   */
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchInsurancePolicies.request({ partnerId: customerId }))

    if (canViewInsuranceProducts) {
      asyncDispatchWithErrorToast(AdminFetchInsuranceProducts.request())
    }
  }, [
    asyncDispatchWithErrorToast,
    isAuthenticated,
    customerId,
    canViewInsuranceProducts,
    refreshToken,
  ])

  // "Settled for THIS customer", not "a request is in flight": gating on `loading` would blank
  // the page on every refresh and on each post-mutation refetch. Without this gate, the layout
  // withholds its outlet only on the customerProduct slice settling — this page would still
  // mount and render once with the PREVIOUS customer's `insurancePolicy.policies` before its own
  // fetch effect dispatches.
  const havePoliciesSettled = loadedPartnerId === customerId

  const isDeletingSelectedPolicy =
    policyPendingDelete !== null && deletingPermanentId === policyPendingDelete.permanentId

  const handleOpenPolicy = useCallback(
    (policy: InsurancePolicy) => {
      if (!customerId) {
        return
      }

      navigate(buildCustomerInsurancePolicyPath(customerId, policy.permanentId))
    },
    [customerId, navigate],
  )

  const handleOpenCreatePolicy = useCallback(() => {
    setPolicyFormModalState({ mode: 'create', policy: null })
  }, [])

  const handleOpenEditPolicy = useCallback((policy: InsurancePolicy) => {
    setPolicyFormModalState({ mode: 'edit', policy })
  }, [])

  // Guarded on `isSaving`, which covers every dismissal path at once — Cancel, the close
  // button, and a backdrop click. Without it an admin could dismiss a saving form, open
  // another row, and have the first request's success handler close the SECOND dialog and
  // discard its unsaved input.
  const handleClosePolicyFormModal = useCallback(() => {
    if (isSaving) {
      return
    }

    setPolicyFormModalState(null)
  }, [isSaving])

  const handleOpenDeletePolicy = useCallback((policy: InsurancePolicy) => {
    setPolicyPendingDelete(policy)
  }, [])

  const handleCloseDeletePolicyModal = useCallback(() => {
    if (isDeletingSelectedPolicy) {
      return
    }

    setPolicyPendingDelete(null)
  }, [isDeletingSelectedPolicy])

  const handleSubmitPolicy = useCallback(
    async (values: InsurancePolicyFormValues) => {
      if (!policyFormModalState || !customerId) {
        return
      }

      const name = values.name.trim()

      if (policyFormModalState.mode === 'edit') {
        if (!policyFormModalState.policy) {
          return
        }

        const updated = await asyncDispatchWithErrorToast<InsurancePolicy>(
          AdminUpdateInsurancePolicy.request({
            insurancePolicyPermanentId: policyFormModalState.policy.permanentId,
            name,
          }),
        )

        if (!updated) {
          return
        }

        snackbar?.enqueueSnackbar('Insurance policy renamed', { variant: 'success' })
        setPolicyFormModalState(null)
        handleRefresh()

        return
      }

      const created = await asyncDispatchWithErrorToast<InsurancePolicy>(
        AdminCreateInsurancePolicy.request({ partnerId: customerId, name }),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Insurance policy created', { variant: 'success' })
      setPolicyFormModalState(null)
      // The admin's next move is adding products to it — take them straight there rather than
      // leaving them on the list.
      navigate(buildCustomerInsurancePolicyPath(customerId, created.permanentId))
    },
    [
      policyFormModalState,
      customerId,
      asyncDispatchWithErrorToast,
      snackbar,
      navigate,
      handleRefresh,
    ],
  )

  const handleConfirmDeletePolicy = useCallback(async () => {
    if (!policyPendingDelete) {
      return
    }

    // A rejected delete (400 'Insurance policy is in use') resolves to null here, and the error
    // toast has already been raised — the modal stays open so the admin can read it against the
    // row they picked.
    const deleted = await asyncDispatchWithErrorToast<string>(
      AdminDeleteInsurancePolicy.request({
        insurancePolicyPermanentId: policyPendingDelete.permanentId,
      }),
    )

    if (!deleted) {
      return
    }

    snackbar?.enqueueSnackbar('Insurance policy deleted', { variant: 'success' })
    setPolicyPendingDelete(null)
    handleRefresh()
  }, [policyPendingDelete, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  return (
    <Fragment>
      <div data-testid="admin-customer-insurance-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">Insurance policies</Typography>
          </div>
          {canManagePolicies ? (
            <Button
              size="small"
              color="dark"
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreatePolicy}
              data-testid="insurance-policies-list-add-button"
            >
              New policy
            </Button>
          ) : null}
        </div>
        {policiesError ? (
          <div
            data-testid="admin-customer-insurance-error"
            css={tw`mb-4 flex flex-row items-center gap-3 rounded-lg border border-solid border-shades-100 bg-shades-50 px-4 py-3`}
          >
            <Typography variant="body2" css={tw`text-shades-700`}>
              {/* The separator is not decoration: backend messages carry no terminal
                  punctuation, so without it this reads as one run-on sentence. */}
              {`Could not load this customer's insurance policies: ${policiesError}`}
            </Typography>
            <div css={tw`ml-auto`}>
              <Button
                size="tiny"
                color="white"
                onClick={handleRefresh}
                data-testid="admin-customer-insurance-retry-button"
              >
                Retry
              </Button>
            </div>
          </div>
        ) : null}
        <InsurancePoliciesTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        {!havePoliciesSettled ? (
          <div css={tw`bg-shades-0 p-4`}>
            <TextSkeleton lines={3} dataTestId="admin-customer-insurance-loading" />
          </div>
        ) : null}
        {havePoliciesSettled ? (
          <InsurancePoliciesTable
            searchQuery={searchQuery}
            canManage={canManagePolicies}
            onOpenPolicy={handleOpenPolicy}
            onEditPolicy={handleOpenEditPolicy}
            onDeletePolicy={handleOpenDeletePolicy}
          />
        ) : null}
      </div>
      <InsurancePolicyFormModal
        isOpen={Boolean(policyFormModalState)}
        mode={policyFormModalState?.mode ?? 'create'}
        policy={policyFormModalState?.policy ?? null}
        isSaving={isSaving}
        onSubmit={handleSubmitPolicy}
        onClose={handleClosePolicyFormModal}
      />
      <DeleteInsurancePolicyModal
        isOpen={Boolean(policyPendingDelete)}
        policy={policyPendingDelete}
        isDeleting={isDeletingSelectedPolicy}
        onConfirm={handleConfirmDeletePolicy}
        onClose={handleCloseDeletePolicyModal}
      />
    </Fragment>
  )
}

export const CustomerInsurancePage = memo(CustomerInsurancePageComponent)
